import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import ScrollToTop from '@/components/base/ScrollToTop';
import { getComicById, getRelatedComics, categories } from '@/mocks/comics';

export default function ComicDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const comic = useMemo(() => (id ? getComicById(id) : undefined), [id]);
  const relatedComics = useMemo(() => (comic ? getRelatedComics(comic, 4) : []), [comic]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
            <p className="text-foreground-500 mb-4">漫画未找到</p>
            <Link
              to="/category/all"
              className="inline-block px-6 py-2 bg-primary-500 text-white rounded-full text-sm font-medium whitespace-nowrap"
            >
              返回漫画列表
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryName = categories.find((c) => c.slug === comic.category)?.name || comic.category;
  const breadcrumbItems = [
    { label: t('breadcrumb.category'), path: '/category/all' },
    { label: comic.title },
  ];

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Article"
        data={{
          headline: `${comic.title} - 好看的韩国漫画`,
          description: comic.description,
          author: { '@type': 'Person', name: comic.author },
          datePublished: comic.createdAt,
          dateModified: comic.updatedAt,
          url: window.location.origin + `/comic/${comic.id}`,
          image: comic.cover,
          publisher: { '@type': 'Organization', name: '韩漫漫画网站', url: window.location.origin },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          <article className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
            {/* Comic Info Header */}
            <div className="flex flex-col md:flex-row gap-8 mb-12">
              <div className="w-full md:w-72 shrink-0">
                <div className="aspect-[2/3] rounded-xl overflow-hidden bg-background-100">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 好看的韩国漫画封面`}
                    title={`${comic.title} - 好看的韩国漫画 在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <div className="flex items-center gap-1 text-accent-500">
                    <i className="ri-star-fill"></i>
                    <span className="font-medium">{comic.rating}</span>
                    <span className="text-foreground-400 text-sm ml-1">{t('comic.rating')}</span>
                  </div>
                  <span className="text-sm text-foreground-500">
                    {t('comic.author')}：{comic.author}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    comic.status === 'completed'
                      ? 'bg-accent-100 text-accent-800'
                      : 'bg-primary-100 text-primary-700'
                  }`}>
                    {comic.status === 'completed' ? t('comic.completed') : t('comic.serializing')}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-foreground-500">
                  <span>
                    <strong className="text-foreground-700">{t('comic.views')}：</strong>
                    {(comic.views / 10000).toFixed(1)}万
                  </span>
                  <span>
                    <strong className="text-foreground-700">{t('comic.updated')}：</strong>
                    {comic.updatedAt}
                  </span>
                  <span>
                    <strong className="text-foreground-700">分类：</strong>
                    {categoryName}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mb-6">
                  <h2 className="font-heading text-lg text-foreground-950 mb-3">{t('comic.description')}</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <Link
                  to={`/comic/${comic.id}`}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-medium text-base transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  {t('comic.startReading')}
                </Link>
              </div>
            </div>

            {/* Chapter List */}
            <section className="mb-12">
              <h2 className="font-heading text-xl text-foreground-950 mb-5">
                {t('comic.chapterList')} ({comic.chapters.length})
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-xl divide-y divide-background-200/50">
                {comic.chapters.map((chapter) => (
                  <Link
                    key={chapter.id}
                    to={`/comic/${comic.id}`}
                    className="flex items-center justify-between px-5 py-4 hover:bg-primary-50/50 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <i className="ri-file-text-line text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
                      <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors font-medium">
                        {chapter.title}
                      </span>
                    </div>
                    <span className="text-xs text-foreground-400">{chapter.updatedAt}</span>
                  </Link>
                ))}
              </div>
            </section>

            {/* Related Comics */}
            {relatedComics.length > 0 && (
              <section>
                <h2 className="font-heading text-xl text-foreground-950 mb-5">{t('comic.related')}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
                  {relatedComics.map((rc) => (
                    <ComicCard key={rc.id} comic={rc} />
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}